import { Link } from "react-router-dom";
import { Shield, Mail, Globe } from "lucide-react";

/**
 * Yol Hattı Gizlilik Politikası
 * Public sayfa — login gerektirmez. Google Play Console incelemesi için.
 * URL: /gizlilik-politikasi
 */
export default function PrivacyPolicy() {
  const lastUpdated = "14 Eylül 2026";

  return (
    <main
      data-testid="privacy-policy-page"
      className="min-h-screen bg-white text-neutral-900"
    >
      {/* Hero */}
      <section className="bg-[hsl(var(--brand-black))] text-white">
        <div className="mx-auto max-w-3xl px-5 py-14 sm:py-16">
          <div className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1 rounded-full bg-primary text-[hsl(var(--brand-black))] mb-5">
            <Shield className="w-3.5 h-3.5" /> Gizlilik Politikası
          </div>
          <h1
            data-testid="privacy-policy-title"
            className="text-4xl sm:text-5xl font-black tracking-tight leading-tight"
          >
            Yol Hattı <span className="text-primary bg-[hsl(var(--brand-black))]">Gizlilik Politikası</span>
          </h1>
          <p className="mt-4 text-sm text-neutral-300">
            Son güncelleme tarihi:{" "}
            <span data-testid="privacy-policy-updated" className="font-semibold text-white">
              {lastUpdated}
            </span>
          </p>
          <p className="mt-2 text-sm text-neutral-300">
            Bu politika <b>yolhatti.com</b> web platformunu ve <b>&ldquo;Yol Hattı Şoför&rdquo;</b> mobil uygulamasını kapsar.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="mx-auto max-w-3xl px-5 py-10 sm:py-14 space-y-10 leading-relaxed text-[15px] sm:text-base">
        {/* 1 */}
        <Section n="1" title="Veri Sorumlusu ve Hizmet">
          <p>
            Yol Hattı; yol yardım, oto kurtarma, taşıma ve ilgili araç hizmetleri için
            müşteriler, hizmet sağlayıcılar ve şoförleri bir araya getiren bir platformdur.
            Bu politika, Yol Hattı platformu ve Yol Hattı Şoför mobil uygulaması aracılığıyla
            işlenen kişisel verilerin nasıl toplandığını, kullanıldığını ve korunduğunu açıklar.
          </p>
        </Section>

        {/* 2 */}
        <Section n="2" title="Toplanabilecek Hesap ve Profil Bilgileri">
          <p>
            Platformu kullanabilmeniz için hesap bilgileri işlenir. Bu bilgiler; ad ve
            soyad, e-posta adresi, telefon numarası, kullanıcı rolü (müşteri / hizmet
            sağlayıcı / şoför), hizmet sağlayıcılar için firma/işletme bilgileri ve
            uygulamanın çalışması için gerekli hesap ayarlarını içerir.
          </p>
        </Section>

        {/* 3 */}
        <Section n="3" title="Konum Verileri">
          <p>
            <b>Yol Hattı Şoför</b> uygulaması, şoförün <b>HATTAYIM/HATTA</b> durumunu
            etkinleştirdiği süre boyunca cihazın hassas konumunu kullanabilir. Bu
            konum bilgileri; canlı filo görünümü, yakındaki hizmet arama, görev
            yönlendirmesi ve aktif görevin doğru takibi amacıyla sunuculara iletilebilir.
          </p>
        </Section>

        {/* 4 */}
        <Section n="4" title="Arka Planda Konum">
          <p>
            Şoför HATTAYIM/HATTA durumundayken uygulama arka plandayken veya cihaz
            ekranı kapalıyken de konum erişimi gerekebilir. Bunun amacı; şoförün
            aktif hizmet konumunun güncel tutulması, müşterilerin yakındaki aktif
            hizmet araçlarını görebilmesi ve yeni görevlerin en uygun şoförlere
            yönlendirilmesidir.
          </p>
          <p>
            Şoför HATTAYIM/HATTA durumunu kapattığında arka plandaki konum takibi
            sona erer. Ayrıca konum izni cihazın sistem ayarlarından her zaman geri
            alınabilir; bu durumda konuma bağlı özellikler kullanılamaz.
          </p>
        </Section>

        {/* 5 */}
        <Section n="5" title="Görev ve Hizmet Verileri">
          <p>
            Platform üzerinden oluşturulan taşıma ve yol yardım talepleri kapsamında
            aşağıdaki bilgiler işlenebilir: talep içeriği, görev durumu, araç bilgileri,
            başlangıç/varış noktaları, teklif ve anlaşılan fiyat, görev geçmişi ve
            hizmetle ilgili operasyonel kayıtlar.
          </p>
        </Section>

        {/* 6 */}
        <Section n="6" title="Bildirimler ve Cihaz Bildirim Kimlikleri">
          <p>
            Yeni görevlerin duyurulması, görev güncellemeleri ve hizmetle ilgili
            önemli anlık bildirimlerin iletilebilmesi için cihaz bildirim
            teknolojileri (ör. Firebase Cloud Messaging) ve cihaza özel bildirim
            (push) tokenları kullanılabilir. Bu tokenlar yalnızca bildirim gönderimi
            için kullanılır ve reklam amacıyla üçüncü taraflara satılmaz.
          </p>
        </Section>

        {/* 7 */}
        <Section n="7" title="İletişim">
          <p>
            Görevlerin yürütülmesi amacıyla gerekli durumlarda müşteri ile hizmet
            sağlayıcı/şoför arasında iletişim bilgileri işlenebilir. Platformun
            numara maskeleme özelliğinin devrede olduğu alanlarda gerçek telefon
            numaralarının gizliliğinin korunması hedeflenir. Bu özellik yalnızca
            aktif olarak devreye alındığı alanlarda geçerlidir. Hizmet sağlayıcı
            işletmelerin kendi profillerine girdikleri işletme telefonu ve WhatsApp
            numarası, müşterilerin doğrudan ulaşabilmesi için herkese açık olarak gösterilir.
          </p>
        </Section>

        {/* 8 */}
        <Section n="8" title="Verilerin Kullanım Amaçları">
          <p>Yukarıda belirtilen veriler aşağıdaki amaçlarla işlenir:</p>
          <ul className="list-disc pl-6 space-y-1.5 mt-2">
            <li>Hizmeti sağlama ve sürdürme,</li>
            <li>Hesap oluşturma, kimlik doğrulama ve oturum yönetimi,</li>
            <li>Görev eşleştirme, yönlendirme ve canlı filo/konum özellikleri,</li>
            <li>Hizmet güvenliği, dolandırıcılık ve kötüye kullanımın önlenmesi,</li>
            <li>Müşteri desteği ve talep/şikâyet yönetimi,</li>
            <li>Yasal yükümlülüklerin yerine getirilmesi,</li>
            <li>Hizmet kalitesinin ve kullanıcı deneyiminin geliştirilmesi.</li>
          </ul>
        </Section>

        {/* 9 */}
        <Section n="9" title="Veri Paylaşımı">
          <p>
            <b>Yol Hattı kişisel verilerinizi satmaz.</b> Verileriniz yalnızca aşağıdaki
            durumlarda ve gerektiği ölçüde paylaşılır:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mt-2">
            <li>
              Hizmetin çalışması için zorunlu olan teknik hizmet sağlayıcılar (ör.
              barındırma, veri tabanı, bildirim altyapısı) ile sınırlı paylaşım,
            </li>
            <li>
              Yasal zorunluluk halinde yetkili kamu kurumları ve yargı makamlarıyla
              paylaşım,
            </li>
            <li>
              Bir görev/hizmet kapsamında yalnızca o göreve ilişkin gerekli
              bilgilerin ilgili müşteri, hizmet sağlayıcı veya şoför ile paylaşımı.
            </li>
          </ul>
        </Section>

        {/* 10 */}
        <Section n="10" title="Veri Güvenliği">
          <p>
            Yetkisiz erişim, kayıp, değiştirme ve kötüye kullanıma karşı makul teknik
            ve idari tedbirler uygulanır. Parolalar şifrelenmiş biçimde saklanır,
            oturum yönetimi güvenli çerezler/token'lar ile yürütülür ve trafik HTTPS
            üzerinden korunur.
          </p>
        </Section>

        {/* 11 */}
        <Section n="11" title="Veri Saklama ve Silme">
          <p>
            Kişisel veriler, hizmetin sağlanması için gerekli süre boyunca ve ilgili
            mevzuatın öngördüğü saklama süreleri kadar tutulur. Kullanıcılar, hesap
            veya belirli verilerinin silinmesine yönelik taleplerini aşağıdaki
            iletişim adresinden iletebilir. Talep, yasal saklama yükümlülükleri
            saklı kalmak kaydıyla değerlendirilir.
          </p>
          <p>
            Şoför ve müşteri hesapları, uygulama üzerinden veya{" "}
            <a
              href="/hesap-silme"
              data-testid="privacy-account-delete-link"
              className="text-primary underline underline-offset-4 font-semibold"
            >
              yolhatti.com/hesap-silme
            </a>{" "}
            adresindeki güvenli sayfa aracılığıyla oturum açıp kalıcı olarak
            silinebilir. Silme talebi sonucunda kişisel veriler (ad, soyad,
            e-posta, telefon, parola özeti ve bildirim tokenları) sunucudan
            kaldırılır; yasal saklama zorunluluğu bulunan tamamlanmış iş
            kayıtlarındaki kimlik referansı anonimleştirilir.
          </p>
        </Section>

        {/* 12 */}
        <Section n="12" title="Kullanıcı Hakları (KVKK)">
          <p>
            6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında kullanıcılar;
            kişisel verilerinin işlenip işlenmediğini öğrenme, işlenmişse buna ilişkin
            bilgi talep etme, verilerin düzeltilmesini veya silinmesini/yok edilmesini
            isteme ve ilgili diğer yasal haklarını kullanma hakkına sahiptir.
            Başvurular bu politikanın sonundaki iletişim adresine yapılabilir.
          </p>
        </Section>

        {/* 13 */}
        <Section n="13" title="Çocukların Gizliliği">
          <p>
            Yol Hattı Şoför uygulaması çocuklara yönelik değildir. Uygulama,
            profesyonel yol yardım/kurtarma/taşıma hizmeti veren yetişkin
            kullanıcılar için tasarlanmıştır. Bilgisi dışında çocuklara ait
            kişisel verilerin toplandığı fark edildiğinde, ilgili veriler silinir.
          </p>
        </Section>

        {/* 14 */}
        <Section n="14" title="Politika Değişiklikleri">
          <p>
            Bu politika gerektiğinde güncellenebilir. Güncel sürüm her zaman{" "}
            <a
              data-testid="privacy-policy-self-link"
              href="https://yolhatti.com/gizlilik-politikasi"
              className="text-primary underline underline-offset-4 font-semibold"
            >
              https://yolhatti.com/gizlilik-politikasi
            </a>{" "}
            adresinde yayınlanır. Önemli değişiklikler bu sayfada belirtilen "Son
            güncelleme tarihi" güncellenerek duyurulur.
          </p>
        </Section>

        {/* 15 */}
        <Section n="15" title="İletişim">
          <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5">
            <div className="flex items-center gap-2 text-sm">
              <Globe className="w-4 h-4 text-neutral-500" />
              <span className="text-neutral-700">Web sitesi:</span>
              <a
                data-testid="privacy-policy-website"
                href="https://yolhatti.com"
                className="text-primary underline underline-offset-4 font-semibold"
              >
                https://yolhatti.com
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm mt-2">
              <Mail className="w-4 h-4 text-neutral-500" />
              <span className="text-neutral-700">Gizlilik ve veri talepleri:</span>
              <a
                data-testid="privacy-policy-email"
                href="mailto:yolhattitr@gmail.com"
                className="text-primary underline underline-offset-4 font-semibold"
              >
                yolhattitr@gmail.com
              </a>
            </div>
          </div>
        </Section>

        {/* Footer */}
        <div className="pt-4 border-t border-neutral-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-neutral-500">
          <span>© {new Date().getFullYear()} Yol Hattı — Tüm hakları saklıdır.</span>
          <Link
            to="/"
            data-testid="privacy-policy-home-link"
            className="text-[hsl(var(--brand-black))] font-semibold hover:text-primary"
          >
            Ana sayfaya dön →
          </Link>
        </div>
      </article>
    </main>
  );
}

function Section({ n, title, children }) {
  return (
    <section data-testid={`privacy-section-${n}`} className="scroll-mt-20">
      <h2 className="text-lg sm:text-lg font-black text-[hsl(var(--brand-black))] mb-3 flex items-start gap-3">
        <span className="inline-flex shrink-0 items-center justify-center min-w-7 h-7 px-2 rounded-md bg-primary text-[hsl(var(--brand-black))] text-sm font-black">
          {n}
        </span>
        <span>{title}</span>
      </h2>
      <div className="text-neutral-800 space-y-3">{children}</div>
    </section>
  );
}
